"use client";

import { useState, useEffect } from "react";
import { X, FlaskConical, AlertTriangle } from "lucide-react";
import Link from "next/link";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { cn } from "@/lib/utils";

interface BannerState {
  betaBannerEnabled: boolean;
  issuesBannerEnabled: boolean;
  signupEnabled?: boolean;
}

// 2026-08-23 beta — dismissible, localStorage, no layout shift
export function BetaBanner() {
  const t = useT();
  const locale = useLocale();
  const contactHref = localePath(locale, "/contact");
  const [dismissed, setDismissed] = useState(false);
  const [bannerState, setBannerState] = useState<BannerState | null>(null);
  const isVisible =
    bannerState !== null &&
    !dismissed &&
    (bannerState.betaBannerEnabled || bannerState.issuesBannerEnabled);
  // 2026-09-13 canónico animaciones: entrada/salida animate-in/out
  // (paridad CookieConsent — solo transform+opacity, 150ms).
  const { shouldRender, isLeaving } = useAnimatedOpen(isVisible, 150);

  useEffect(() => {
    const controller = new AbortController();
    // Sin no-store: respeta public max-age=60 del server (1 fetch por montaje, no es poll).
    // SEED=2026-09-25-c6c873bf RUN1 FASE4 T4 offline/CPU (research 2026 MDN
    // AbortSignal.timeout() 2026-09-01 vía webfetch: fetch único por montaje
    // sin timeout cuelga el banner en red degradada — timeout 10s combinado
    // con el AbortController de unmount vía AbortSignal.any (fallback a solo
    // controller si AbortSignal.any/timeout no existe). Fail-open intacto
    // (catch → banners apagados).
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let signal: AbortSignal | undefined = controller.signal;
    try {
      if (
        typeof AbortSignal !== "undefined" &&
        "timeout" in AbortSignal &&
        "any" in AbortSignal
      ) {
        signal = AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(10_000),
        ]);
      } else {
        timeoutId = setTimeout(() => controller.abort(), 10_000);
      }
    } catch {}
    fetch("/api/banner", { signal })
      .then((r) => r.json())
      .then((data: BannerState) => setBannerState(data))
      .catch(() =>
        setBannerState({
          betaBannerEnabled: false,
          issuesBannerEnabled: false,
        }),
      )
      .finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  if (!bannerState || !shouldRender) return null;

  const isIssues =
    bannerState.issuesBannerEnabled && !bannerState.betaBannerEnabled;

  return (
    <div
      className="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out"
      style={{ gridTemplateRows: "1fr", opacity: 1 }}
    >
      <div className="overflow-hidden">
        <div
          className={cn(
            // 2026-09-23 FASE 3 FIXER diseño H5 (research 2026 DSP single-source
            // tokens + M3 semantic colors: la cinta gradient 3-paradas era 2ª
            // capa visual — 1 superficie sólida semántica (warning/accent 8% +
            // borde 15%), 1 brillo (sin gradiente). --accent monocromo SSOT,
            // --warning semántico; tinta por tokens).
            isIssues
              ? "relative border-b border-[var(--warning)]/15 bg-[color-mix(in_srgb,var(--warning)_8%,transparent)]"
              : "relative border-b border-[var(--accent)]/15 bg-[color-mix(in_srgb,var(--accent)_8%,transparent)]",
            // 2026-09-13 canónico animaciones: entrada/salida en la cinta
            // (el shell grid no se anima — colapsa el layout).
            isLeaving
              ? "animate-out fade-out slide-out-to-top-1 duration-150 motion-reduce:animate-none"
              : "animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none",
          )}
        >
          <div className="mx-auto flex max-w-7xl items-start justify-center gap-2 px-4 py-2.5 pr-11 text-xs leading-snug text-foreground-soft sm:items-center sm:px-5">
            {isIssues ? (
              <AlertTriangle
                className="mt-0.5 size-3.5 shrink-0 text-[var(--warning)] sm:mt-0"
                aria-hidden="true"
              />
            ) : (
              <FlaskConical
                className="mt-0.5 size-3.5 shrink-0 text-[var(--accent)] sm:mt-0"
                aria-hidden="true"
              />
            )}
            <p className="min-w-0 max-w-[40rem] text-pretty text-center sm:text-left">
              {isIssues ? t("issuesBannerText") : t("betaBannerText")}{" "}
              <Link
                href={contactHref}
                className={cn(
                  "font-medium whitespace-nowrap hover:underline",
                  isIssues ? "text-[var(--warning)]" : "text-[var(--accent)]",
                )}
              >
                {isIssues ? t("issuesBannerContact") : t("betaBannerContact")}
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground hover:bg-[var(--accent)]/10 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            aria-label={
              isIssues ? t("issuesBannerDismiss") : t("betaBannerDismiss")
            }
          >
            {/* WCAG 2.2 2.5.8: p-1.5 + size-3.5 = 26px ≥ 24px target mínimo */}
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
